import { useEffect, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate unique visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('visitor_id', visitorId);
  }
  return visitorId;
};

// Get session ID (new session after 30 min inactivity)
const getSessionId = () => {
  const now = Date.now();
  const lastActivity = parseInt(localStorage.getItem('last_activity') || '0');
  let sessionId = localStorage.getItem('session_id');
  
  // New session if more than 30 minutes of inactivity
  if (!sessionId || (now - lastActivity) > 30 * 60 * 1000) {
    sessionId = 's_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('session_id', sessionId);
    localStorage.setItem('session_start', now.toString());
  }
  
  localStorage.setItem('last_activity', now.toString());
  return sessionId;
};

// Parse user agent to get browser and OS info
const parseUserAgent = () => {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let os = 'Unknown';
  let device = 'Desktop';

  // Detect Browser
  if (ua.includes('Firefox/')) {
    browser = 'Firefox';
  } else if (ua.includes('Edg/')) {
    browser = 'Edge';
  } else if (ua.includes('Chrome/')) {
    browser = 'Chrome';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browser = 'Safari';
  } else if (ua.includes('Opera') || ua.includes('OPR/')) {
    browser = 'Opera';
  }

  // Detect OS
  if (ua.includes('Windows NT 10')) {
    os = 'Windows 10/11';
  } else if (ua.includes('Windows NT')) {
    os = 'Windows';
  } else if (ua.includes('Mac OS X')) {
    os = 'macOS';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  } else if (ua.includes('Android')) {
    os = 'Android';
  } else if (ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS';
  }

  // Detect Device Type
  if (ua.includes('Mobile') || ua.includes('Android') || ua.includes('iPhone')) {
    device = 'Mobile';
  } else if (ua.includes('Tablet') || ua.includes('iPad')) {
    device = 'Tablet';
  }

  return { browser, os, device };
};

// Get location from IP (using backend API to avoid mixed content issues)
const getLocationData = async () => {
  try {
    // Check if we already have cached location data (expires in 1 hour)
    const cachedLocation = localStorage.getItem('visitor_location');
    const cachedTime = localStorage.getItem('visitor_location_time');
    
    if (cachedLocation && cachedTime && (Date.now() - parseInt(cachedTime)) < 3600000) {
      return JSON.parse(cachedLocation);
    }

    // Use backend endpoint to get location (avoids mixed content issues)
    const response = await axios.get(`${API_URL}/api/analytics/get-location`, {
      timeout: 10000
    });
    
    if (response.data && response.data.country) {
      const locationData = {
        country: response.data.country,
        country_code: response.data.country_code,
        region: response.data.region,
        city: response.data.city,
        zip: response.data.zip,
        latitude: response.data.latitude,
        longitude: response.data.longitude,
        timezone: response.data.timezone,
        isp: response.data.isp,
        ip: response.data.ip
      };
      
      // Cache the location data
      localStorage.setItem('visitor_location', JSON.stringify(locationData));
      localStorage.setItem('visitor_location_time', Date.now().toString());
      
      return locationData;
    }
  } catch (error) {
    console.log('Could not fetch location data:', error.message);
  }
  
  return null;
};

export const VisitorTracker = () => {
  const trackingRef = useRef({
    initialized: false,
    scrollDepth: 0,
    timeOnPage: 0,
    pagesViewed: [],
    interactions: []
  });

  useEffect(() => {
    if (trackingRef.current.initialized) return;
    trackingRef.current.initialized = true;

    const visitorId = getVisitorId();
    const sessionId = getSessionId();
    const startTime = Date.now();
    const { browser, os, device } = parseUserAgent();
    
    // Track referrer and landing page
    const trackPageView = async () => {
      try {
        // Get location data
        const location = await getLocationData();
        
        const pageData = {
          visitor_id: visitorId,
          session_id: sessionId,
          page_url: window.location.pathname,
          page_title: document.title,
          referrer: document.referrer || 'direct',
          referrer_host: document.referrer ? new URL(document.referrer).hostname : 'direct',
          user_agent: navigator.userAgent,
          browser: browser,
          os: os,
          device_type: device,
          screen_width: window.screen.width,
          screen_height: window.screen.height,
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          // Location data
          country: location?.country || null,
          country_code: location?.country_code || null,
          region: location?.region || null,
          city: location?.city || null,
          ip_address: location?.ip || null,
          isp: location?.isp || null,
          latitude: location?.latitude || null,
          longitude: location?.longitude || null,
          timestamp: new Date().toISOString()
        };
        
        await axios.post(`${API_URL}/api/analytics/pageview`, pageData);
      } catch (error) {
        console.log('Analytics tracking error:', error);
      }
    };

    // Track scroll depth
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);
      
      if (scrollPercent > trackingRef.current.scrollDepth) {
        trackingRef.current.scrollDepth = scrollPercent;
      }
    };

    // Track clicks on important elements
    const handleClick = (e) => {
      const target = e.target.closest('button, a, [data-track]');
      if (target) {
        const interaction = {
          type: 'click',
          element: target.tagName,
          text: target.textContent?.substring(0, 50),
          testId: target.getAttribute('data-testid'),
          href: target.href || null,
          timestamp: Date.now() - startTime
        };
        trackingRef.current.interactions.push(interaction);
      }
    };

    // Track form interactions
    const handleFormInteraction = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        const interaction = {
          type: 'form_interaction',
          element: e.target.tagName,
          name: e.target.name || e.target.id,
          timestamp: Date.now() - startTime
        };
        trackingRef.current.interactions.push(interaction);
      }
    };

    // Send session data on page unload
    const sendSessionData = async () => {
      const sessionData = {
        visitor_id: visitorId,
        session_id: sessionId,
        page_url: window.location.pathname,
        time_spent_seconds: Math.round((Date.now() - startTime) / 1000),
        scroll_depth: trackingRef.current.scrollDepth,
        interactions: trackingRef.current.interactions.slice(0, 50), // Limit interactions
        exit_time: new Date().toISOString()
      };
      
      // Use sendBeacon for reliable sending on page unload
      const blob = new Blob([JSON.stringify(sessionData)], { type: 'application/json' });
      navigator.sendBeacon(`${API_URL}/api/analytics/session`, blob);
    };

    // Initialize tracking
    trackPageView();
    
    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('click', handleClick);
    document.addEventListener('focus', handleFormInteraction, true);
    window.addEventListener('beforeunload', sendSessionData);

    // Send periodic updates (every 30 seconds)
    const intervalId = setInterval(() => {
      const updateData = {
        visitor_id: visitorId,
        session_id: sessionId,
        page_url: window.location.pathname,
        time_spent_seconds: Math.round((Date.now() - startTime) / 1000),
        scroll_depth: trackingRef.current.scrollDepth
      };
      
      axios.post(`${API_URL}/api/analytics/heartbeat`, updateData).catch(() => {});
    }, 30000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('focus', handleFormInteraction, true);
      window.removeEventListener('beforeunload', sendSessionData);
      clearInterval(intervalId);
      sendSessionData();
    };
  }, []);

  return null; // This component doesn't render anything
};

export default VisitorTracker;
